import React, { useState } from 'react';
import theme from './BottomPanel.module.scss';
import { Accordion, Button, Card, Badge } from '@ygtechnologies/rubus-ui';
import { classnames, DateDistanceDisplay } from '@ygtechnologies/bygr-frontend-component-library';
import { BuyerCurrencyDisplay } from '@/buyer/components/BuyerCurrencyDisplay';
import { faArrowUp } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { BuyerOptionCard } from '../BuyerOptionCard';
import { useOptionConfigurator } from '../OptionConfiguratorContext';
import { ProductCardList } from '@/seller/components/products/ProductCard';
import { useHistory } from 'react-router';
import { buildConfiguratorUrl } from '../utils/buildConfiguratorUrl';
import { useIsBuyer } from '@/utils/hooks';
import { getRoomDisplayName } from '../utils/configuratorRooms';

export interface BottomPanelProps {}

export const BottomPanel: React.FC<BottomPanelProps> = ({}) => {
    const [expanded, setExpanded] = useState(false);
    const { configuratorData, rooms, selectedDeadlineId, projectData, baseUrl, bottomPanelEnabled } = useOptionConfigurator();
    const isBuyer = useIsBuyer();
    const history = useHistory();

    const totalCost = configuratorData?.grandPrice || 0;

    if (!bottomPanelEnabled || !isBuyer) {
        return null;
    }

    if (!projectData) {
        return null;
    }

    const currentDeadline = projectData.deadlines.find((d) => d.id === selectedDeadlineId) || null;
    const numberOfOptions = rooms.reduce((acc, room) => acc + room.groups.length, 0);
    return (
        <>
            <div className={theme.padding}></div>
            <Card className={classnames(theme.bottomPanel, { [theme.expanded]: expanded })}>
                <button className={theme.toggleButton} onClick={() => setExpanded(!expanded)}>
                    <FontAwesomeIcon className={classnames(theme.toggleIcon)} icon={faArrowUp} />
                </button>
                <div className={theme.summary}>
                    {/*TODO: Can use same pill as in the home page when that is merged in*/}
                    {currentDeadline && (
                        <div>
                            <Trans>Deadline:</Trans> <strong>{currentDeadline?.name ?? '-'}</strong>
                            <Badge appearance="subtle">
                                <DateDistanceDisplay value={currentDeadline?.deadline} />
                            </Badge>
                        </div>
                    )}
                    <div className={theme.summaryRight}>
                        <Badge appearance="subtle">
                            <Trans>{numberOfOptions} items</Trans>
                        </Badge>
                        <div className={theme.cost}>
                            <Trans>Total Cost:</Trans> <BuyerCurrencyDisplay value={totalCost} />
                        </div>
                    </div>
                </div>

                <div className={theme.expandedContent}>
                    {rooms.map((room) => (
                        <Accordion
                            key={room.roomId}
                            title={
                                <div className={theme.accordionTitle}>
                                    <div>
                                        <span>{getRoomDisplayName(room)}</span>
                                        <Button
                                            size="sm"
                                            onClick={(e) => {
                                                history.push(
                                                    buildConfiguratorUrl({
                                                        baseUrl,
                                                        deadlineId: selectedDeadlineId,
                                                        segment: `room/${room.roomId}`,
                                                    })
                                                );
                                                setExpanded(false);
                                            }}
                                            variant="tertiary">
                                            <Trans>Go to room</Trans>
                                        </Button>
                                    </div>
                                    <div>
                                        <Badge appearance="subtle">
                                            <Trans>{room.groups.length} items</Trans>
                                        </Badge>
                                        <BuyerCurrencyDisplay value={room.roomPrice} />
                                    </div>
                                </div>
                            }
                            className={theme.accordion}>
                            <ProductCardList>
                                {room.groups
                                    .filter((g) => g.selectedOption !== null)
                                    .map((g, idx) => (
                                        <BuyerOptionCard
                                            key={`${room.roomId}-${idx}`}
                                            option={g.selectedOption!}
                                            isSelected={true}
                                            groupName={g.name}
                                        />
                                    ))}
                            </ProductCardList>
                        </Accordion>
                    ))}
                </div>
            </Card>
        </>
    );
};
